import type { Portfolio, PortfolioInstrument, PortfolioSocketEvent } from '@akem/shared';
import { fetchAuthSession } from 'aws-amplify/auth';
import { create } from 'zustand';
import { getProfileSocketEndpoint } from '../../config/api.js';
import { portfolioService } from '../../services/portfolioService.js';

export type PortfolioSocketStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

interface PortfolioState {
  portfolios: Portfolio[];
  activeId: string | null;
  loading: boolean;
  error?: string;
  initialized: boolean;
  socketStatus: PortfolioSocketStatus;
  socketError?: string;
  load: () => Promise<void>;
  initialize: () => Promise<void>;
  refresh: () => Promise<void>;
  createPortfolio: (payload: { name: string; symbols?: string[]; setDefault?: boolean }) => Promise<Portfolio>;
  updatePortfolio: (portfolioId: string, updates: { name?: string; setDefault?: boolean; expectedVersion?: number }) => Promise<Portfolio>;
  removePortfolio: (portfolioId: string, expectedVersion?: number) => Promise<void>;
  addSymbols: (portfolioId: string, payload: { symbols: string[]; expectedVersion?: number }) => Promise<Portfolio>;
  removeSymbol: (
    portfolioId: string,
    value: string,
    options?: { mode?: 'symbol' | 'key'; expectedVersion?: number }
  ) => Promise<Portfolio>;
  setActive: (portfolioId: string | null) => void;
  ensureSocket: () => Promise<void>;
  disconnectSocket: () => void;
}

let socketRef: WebSocket | null = null;
let reconnectTimer: number | undefined;

const SOCKET_RETRY_MS = 5000;

function computeActiveId(portfolios: Portfolio[], previous?: string | null): string | null {
  if (!portfolios.length) return null;
  if (previous && portfolios.some((p) => p.id === previous)) {
    return previous;
  }
  const defaultPortfolio = portfolios.find((p) => p.isDefault);
  if (defaultPortfolio) {
    return defaultPortfolio.id;
  }
  return portfolios[0]?.id ?? null;
}

function upsertPortfolio(list: Portfolio[], portfolio: Portfolio): Portfolio[] {
  const index = list.findIndex((item) => item.id === portfolio.id);
  if (index === -1) {
    return [...list, portfolio];
  }
  const next = list.slice();
  next[index] = portfolio;
  return next;
}

function removePortfolioById(list: Portfolio[], portfolioId: string): Portfolio[] {
  return list.filter((item) => item.id !== portfolioId);
}

function updateDefaultFlag(list: Portfolio[], portfolioId: string): Portfolio[] {
  return list.map((portfolio) =>
    portfolio.id === portfolioId
      ? { ...portfolio, isDefault: true }
      : { ...portfolio, isDefault: false }
  );
}

function applyInstrumentDiff(
  portfolio: Portfolio,
  instruments?: PortfolioInstrument[],
  fallback?: Portfolio
): Portfolio {
  if (fallback) {
    return fallback;
  }
  if (!instruments) {
    return portfolio;
  }
  return {
    ...portfolio,
    instruments,
  };
}

function handleSocketEvent(state: PortfolioState, event: PortfolioSocketEvent): Partial<PortfolioState> {
  const { portfolios, activeId } = state;
  let nextPortfolios = portfolios;
  let nextActiveId = activeId;

  switch (event.change) {
    case 'created': {
      if (event.portfolio) {
        nextPortfolios = upsertPortfolio(portfolios, event.portfolio);
        nextActiveId = computeActiveId(nextPortfolios, activeId);
      }
      break;
    }
    case 'renamed':
    case 'updated':
    case 'instrumentsAdded':
    case 'instrumentsRemoved':
    case 'setDefault': {
      if (event.portfolio) {
        const updated =
          event.change === 'setDefault'
            ? updateDefaultFlag(upsertPortfolio(portfolios, event.portfolio), event.portfolio.id)
            : upsertPortfolio(portfolios, event.portfolio);
        nextPortfolios = updated;
        nextActiveId = computeActiveId(updated, activeId);
      } else {
        // Fallback: update instrument list if provided
        const existing = portfolios.find((p) => p.id === event.portfolioId);
        if (existing) {
          const patched = applyInstrumentDiff(existing, event.instruments);
          if (patched !== existing) {
            nextPortfolios = upsertPortfolio(portfolios, patched);
          }
        }
      }
      break;
    }
    case 'deleted': {
      nextPortfolios = removePortfolioById(portfolios, event.portfolioId);
      nextActiveId = computeActiveId(nextPortfolios, nextActiveId);
      break;
    }
    default:
      break;
  }

  return {
    portfolios: nextPortfolios,
    activeId: nextActiveId,
  };
}

export const usePortfolioStore = create<PortfolioState>((set, get) => ({
  portfolios: [],
  activeId: null,
  loading: false,
  error: undefined,
  initialized: false,
  socketStatus: 'disconnected',
  socketError: undefined,

  load: async () => {
    set({ loading: true, error: undefined });
    try {
      const data = await portfolioService.list();
      set((state) => ({
        portfolios: data,
        activeId: computeActiveId(data, state.activeId),
        loading: false,
      }));
    } catch (error) {
      set({ loading: false, error: (error as Error)?.message ?? 'Failed to load portfolios' });
      throw error;
    }
  },

  initialize: async () => {
    if (get().initialized) return;
    await get().load();
    await get().ensureSocket();
    set({ initialized: true });
  },

  refresh: async () => {
    await get().load();
  },

  createPortfolio: async (payload) => {
    const portfolio = await portfolioService.create(payload);
    set((state) => {
      const updated = upsertPortfolio(state.portfolios, portfolio);
      const active = state.portfolios.length === 0
        ? portfolio.id
        : computeActiveId(updated, state.activeId);
      return {
        portfolios: updated,
        activeId: active,
      };
    });
    return portfolio;
  },

  updatePortfolio: async (portfolioId, updates) => {
    const portfolio = await portfolioService.update(portfolioId, updates);
    set((state) => {
      const updatedList = updates.setDefault
        ? updateDefaultFlag(upsertPortfolio(state.portfolios, portfolio), portfolio.id)
        : upsertPortfolio(state.portfolios, portfolio);
      return {
        portfolios: updatedList,
        activeId: computeActiveId(updatedList, state.activeId),
      };
    });
    return portfolio;
  },

  removePortfolio: async (portfolioId, expectedVersion) => {
    await portfolioService.remove(portfolioId, expectedVersion);
    set((state) => {
      const next = removePortfolioById(state.portfolios, portfolioId);
      return {
        portfolios: next,
        activeId: computeActiveId(next, state.activeId === portfolioId ? null : state.activeId),
      };
    });
  },

  addSymbols: async (portfolioId, payload) => {
    const portfolio = await portfolioService.addInstruments(portfolioId, payload);
    set((state) => ({
      portfolios: upsertPortfolio(state.portfolios, portfolio),
    }));
    return portfolio;
  },

  removeSymbol: async (portfolioId, value, options) => {
    const portfolio = await portfolioService.removeInstrument(portfolioId, value, options);
    set((state) => ({
      portfolios: upsertPortfolio(state.portfolios, portfolio),
    }));
    return portfolio;
  },

  setActive: (portfolioId) => {
    set({ activeId: portfolioId });
  },

  ensureSocket: async () => {
    const endpoint = getProfileSocketEndpoint();
    if (!endpoint) {
      set({ socketStatus: 'error', socketError: 'Missing profile socket endpoint' });
      return;
    }

    if (socketRef && (socketRef.readyState === WebSocket.OPEN || socketRef.readyState === WebSocket.CONNECTING)) {
      return;
    }

    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = undefined;
    }

    set({ socketStatus: 'connecting', socketError: undefined });

    let token: string | undefined;
    try {
      const session = await fetchAuthSession();
      token = session.tokens?.idToken?.toString();
    } catch (error) {
      console.warn('[portfolioStore] Failed to fetch auth session for socket', error);
    }

    try {
      const url = new URL(endpoint);
      if (token) {
        url.searchParams.set('token', token);
      }

      socketRef = new WebSocket(url.toString());

      socketRef.onopen = () => {
        try {
          console.warn('[portfolioStore] socket connected', { endpoint: url.toString() });
        } catch {}
        set({ socketStatus: 'connected', socketError: undefined });
      };

      socketRef.onmessage = (event) => {
        try {
          const parsed = JSON.parse(event.data);
          if (parsed?.type === 'portfolioUpdated') {
            set((state) => handleSocketEvent(state, parsed as PortfolioSocketEvent));
          }
        } catch (error) {
          console.warn('[portfolioStore] Failed to parse socket message', error);
        }
      };

      socketRef.onerror = (err) => {
        console.warn('[portfolioStore] Socket error', err);
        set({ socketStatus: 'error', socketError: 'Socket error' });
      };

      socketRef.onclose = (event: CloseEvent) => {
        try {
          console.warn('[portfolioStore] socket closed', {
            code: event?.code,
            reason: event?.reason,
            wasClean: event?.wasClean,
          });
        } catch {}
        set({ socketStatus: 'disconnected' });
        socketRef = null;
        reconnectTimer = window.setTimeout(() => {
          get().ensureSocket().catch(() => {
            // Swallow errors here; ensureSocket will set state on failure
          });
        }, SOCKET_RETRY_MS);
      };
    } catch (error) {
      set({ socketStatus: 'error', socketError: (error as Error)?.message });
      socketRef = null;
      reconnectTimer = window.setTimeout(() => {
        get().ensureSocket().catch(() => {
          // handled in ensureSocket
        });
      }, SOCKET_RETRY_MS);
    }
  },

  disconnectSocket: () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = undefined;
    }
    if (socketRef) {
      socketRef.close();
      socketRef = null;
    }
    set({ socketStatus: 'disconnected', socketError: undefined });
  },
}));

export const selectActivePortfolio = (state: PortfolioState) =>
  state.portfolios.find((portfolio) => portfolio.id === state.activeId) ?? null;

export const selectPortfolios = (state: PortfolioState) => state.portfolios;

export const selectPortfolioLoading = (state: PortfolioState) => state.loading;

export const selectPortfolioError = (state: PortfolioState) => state.error;

export const selectPortfolioSocketStatus = (state: PortfolioState) => state.socketStatus;
